import { createContext, useContext } from 'react';

import { InstrumentGrid } from './InstrumentGrid';

/**
 * True once a GridBackground is mounted anywhere above us.
 *
 * `InstrumentGrid` is `fixed inset-0`, so a nested instance covers the whole
 * viewport regardless of its parent - two of them paint the lattice twice, at
 * roughly double alpha. The app shell already mounts one in `app/provider.tsx`
 * and every route renders inside it.
 *
 * A context flag rather than deleting the nested call sites, because one case
 * is only reachable at runtime: the bare `<ErrorBoundary>`s in `app/routes.tsx`
 * pass no `fallback`, so on a crash they render the root fallback - which has
 * its OWN GridBackground - inside the shell's. That can't be seen in the static
 * tree. The root `<ErrorBoundary>` in `App.tsx` sits ABOVE the providers, so
 * when it catches, the shell backdrop is unmounted and it genuinely needs its
 * own; this guard leaves that case working.
 */
const BackdropMounted = createContext(false);

interface GridBackgroundProps {
  /** Additional CSS classes */
  className?: string;
  /** Children to render on top */
  children?: React.ReactNode;
  /** Whether to center the content (for backwards compatibility) */
  centerContent?: boolean;
}

/**
 * The app's page backdrop: a flat `--ground` fill plus the instrument lattice.
 *
 * Both themes get exactly those two layers. Light used to carry three paper
 * texture layers and dark a violet bloom, so the two themes had different
 * character rather than different values; both are gone. The bloom was also
 * `absolute inset-0` against the lattice's `fixed inset-0`, so on a long page
 * it stretched to document height and scrolled while the lattice stayed put.
 */
export function GridBackground({
  className = '',
  children,
  centerContent = false,
}: GridBackgroundProps) {
  const nested = useContext(BackdropMounted);

  const tree = (
    <div className={`relative ${className}`}>
      {!nested && <InstrumentGrid />}

      {children && (
        <div
          className={`relative z-10 w-full ${centerContent ? 'flex min-h-screen items-center justify-center' : ''}`}
        >
          {children}
        </div>
      )}
    </div>
  );

  return nested ? (
    tree
  ) : (
    <BackdropMounted.Provider value={true}>{tree}</BackdropMounted.Provider>
  );
}
